
import React from 'react';
import { AgentTemplate } from '@/data/agentTemplates';
import { getApiConfiguration, getRunAgentFunction, type ApiProvider } from '@/utils/agent-generator/providerUtils';
import { generateDocumentIntegration } from '@/utils/agent-generator/documentUtils';
import { generateSystemPrompt } from '@/utils/agent-generator/systemPromptUtils';

interface AgentCodeGeneratorProps {
  selectedTemplate: string;
  agentName: string;
  agentSettings: Record<string, string>;
  uploadedDocuments: File[];
  userId: string;
  provider: string;
}

export const generateAgentCode = ({
  selectedTemplate,
  agentName,
  agentSettings,
  uploadedDocuments,
  userId,
  provider = 'openai'
}: AgentCodeGeneratorProps): string => {
  const template = AGENT_TEMPLATES.find(t => t.id === selectedTemplate);
  
  // Get document integration code
  const documentIntegration = generateDocumentIntegration(uploadedDocuments);
  
  // Get API configuration based on provider
  const { apiImport, apiInitialization, apiModelParam } = getApiConfiguration(provider as ApiProvider);
  
  // Get provider-specific run function
  const runAgentFunction = getRunAgentFunction(provider as ApiProvider, apiModelParam);

  const generatedCodeSample = `
// Agent: ${agentName}
// Template: ${template?.name}
// Created by: User ${userId}
// Provider: ${provider}

${apiImport}

${apiInitialization}

// Agent configuration
const agentConfig = {
  name: "${agentName}",
  template: "${template?.name}",
  settings: ${JSON.stringify(agentSettings, null, 2)}
};
${documentIntegration}

async function runAgent(userInput) {
  // Construct a domain-specific system prompt based on the template and settings
  let systemPrompt = "${generateSystemPrompt(selectedTemplate, agentSettings).replace(/"/g, '\\"').replace(/`/g, '\\`')}";

  ${runAgentFunction}
}

// Example of how to use the agent
async function demo() {
  const answer = await runAgent("Hello, can you help me follow the procedure?");
  console.log(answer);
}

// Usage instructions:
// 1. Set your API key as an environment variable
// 2. Call the runAgent function with user input
// 3. Handle the response appropriately in your application
`;

  return generatedCodeSample;
};

// Import here to avoid circular dependency
import AGENT_TEMPLATES from '@/data/agentTemplates';

export default generateAgentCode;
